/**
 * 音频参考上传节点 - 用于音频生成或处理的参考音频
 */
import React, { memo, useState, useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';
import { NodeLayout } from './NodeLayout';
import { useProject } from '../providers/ProjectProvider';
import { useDropzone } from 'react-dropzone';
import ApiService from '../../../api/ApiService';
import { toast } from 'react-toastify';
import './AudioReferenceNode.css';

const apiService = new ApiService();

export const AudioReferenceNode = memo(({ id, data }) => {
  const { updateNodeData } = useReactFlow();
  const { readOnly } = useProject();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  // 判断是否为本地文件URL（data: 或 blob:）
  const isLocalFileUrl = (url) => {
    if (!url) return false;
    return url.startsWith('data:') || url.startsWith('blob:');
  };

  // 处理音频上传
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setUploading(true);
      setUploadProgress(0);
      setError(null);

      try {
        // 1. 先显示本地预览
        const reader = new FileReader();
        reader.onload = () => {
          const previewUrl = reader.result;
          updateNodeData(id, {
            audioUrl: previewUrl,
            fileName: file.name,
            isUploading: true,
          });
        };
        reader.readAsDataURL(file);

        setUploadProgress(30);
        console.log('开始上传音频到R2...', file.name);

        // 2. 上传到 Cloudflare R2 (使用通用的上传方法)
        const uploadResult = await apiService.uploadImageToR2(file);
        
        setUploadProgress(90);
        console.log('上传成功，公开URL:', uploadResult.public_url);

        // 3. 更新节点数据为云端URL
        updateNodeData(id, {
          audioUrl: uploadResult.public_url,
          fileName: file.name,
          fileKey: uploadResult.file_key,
          isUploading: false,
        });

        setUploadProgress(100);
        toast.success('音频上传成功');
      } catch (error) {
        console.error('上传失败:', error);
        const errorMessage = error.message || '上传失败，请重试';
        setError(errorMessage);
        toast.error(errorMessage);
        
        // 清除预览音频
        updateNodeData(id, {
          audioUrl: undefined,
          fileName: undefined,
          fileKey: undefined,
          isUploading: false,
        });
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [id, updateNodeData]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav'],
      'audio/ogg': ['.ogg'],
      'audio/mp4': ['.m4a'],
      'audio/aac': ['.aac'],
      'audio/flac': ['.flac'],
      'audio/x-ms-wma': ['.wma'],
    },
    maxFiles: 1,
    disabled: readOnly,
  });

  // 清除音频
  const handleClear = () => {
    if (readOnly) return;
    updateNodeData(id, {
      audioUrl: undefined,
      fileName: undefined,
      fileKey: undefined,
      isUploading: false,
    });
    setError(null);
  };

  return (
    <NodeLayout 
      id={id} 
      type="audioReference" 
      title="音频参考"
      showInputHandle={false}
    >
      <div className="AudioReferenceNode-wrapper">
        {/* 错误提示 */}
        {error && (
          <div className="AudioReferenceNode-error">
            {error}
          </div>
        )}
        
        {data.audioUrl ? (
          // 显示已上传的音频
          <div className="AudioReferenceNode-preview">
            <div className="AudioReferenceNode-player-container">
              <audio
                src={data.audioUrl}
                className="AudioReferenceNode-audio"
                controls
                preload="metadata"
              >
                您的浏览器不支持音频播放
              </audio>
            </div>
            {!data.isUploading && !readOnly && (
              <button
                onClick={handleClear}
                className="AudioReferenceNode-clear"
                title="删除音频"
              >
                ×
              </button>
            )}
            <div className="AudioReferenceNode-info">
              {data.isUploading && (
                <div className="AudioReferenceNode-upload-status">
                  <div className="AudioReferenceNode-loading-small" />
                  <span>上传中...</span>
                </div>
              )}
              {data.fileName && (
                <div className="AudioReferenceNode-filename">
                  {data.fileName}
                </div>
              )}
              {!data.isUploading && isLocalFileUrl(data.audioUrl) && (
                <div className="AudioReferenceNode-warning">
                  ⚠️ 本地预览（未上传）
                </div>
              )}
            </div>
          </div>
        ) : (
          // 上传区域
          <div
            {...getRootProps()}
            className={`AudioReferenceNode-dropzone ${isDragActive ? 'AudioReferenceNode-dropzone-active' : ''}`}
          >
            <input {...getInputProps()} />
            <div className="AudioReferenceNode-dropzone-content">
              {uploading ? (
                <>
                  <div className="AudioReferenceNode-loading" />
                  <div className="AudioReferenceNode-text">
                    上传中 {uploadProgress > 0 ? `${uploadProgress}%` : '...'}
                  </div>
                </>
              ) : (
                <>
                  <svg className="AudioReferenceNode-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M8 10 C8 8.9 8.9 8 10 8 L18 8 L24 14 L24 24 C24 25.1 23.1 26 22 26 L10 26 C8.9 26 8 25.1 8 24 Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <path d="M18 8 L18 14 L24 14" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <circle cx="13" cy="18" r="2" fill="currentColor"/>
                    <path d="M15 16 L15 20" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M17 15 L17 21" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M19 17 L19 19" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                  <div className="AudioReferenceNode-text">
                    {isDragActive ? '放开以上传音频' : '拖拽音频到这里或点击上传'}
                  </div>
                  <div className="AudioReferenceNode-hint">
                    支持 MP3、WAV、OGG、M4A 等格式
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </NodeLayout>
  );
});

AudioReferenceNode.displayName = 'AudioReferenceNode';


